import { useAtom } from 'jotai';
import { approvalFilter } from '@/recoil/atom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import LeftContainer from '@/components/Approval/LeftContainer';
import RightContainer from '@/components/Approval/RightContainer';
import { FormProvider, useForm } from 'react-hook-form';
import HoverLeftDrawerLayout from '@/components/HoverLeftDrawerLayout';
import { getIntranetDocUrl } from '@/utils/urls';
import { SearchData } from '@/types/api';
import ApprovalModal from '@/components/Approval/ApprovalModal';
import NextApprovalConfirmModal from '@/components/Approval/NextApprovalConfirmModal';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import useModal from '@/hooks/useModal';
import useApproval from '@/components/Approval/@hooks/useApproval';
import useApprovalFilterLink from '@/components/Approval/@hooks/useApprovalFilterLink';
import useNextApprovalDoc from '@/components/Approval/@hooks/useNextApprovalDoc';
import type { NextApprovalDoc } from '@/components/Approval/@hooks/useNextApprovalDoc';
import useAuthorStatusBadgeDot from '@/hooks/approval/useAuthorStatusBadgeDot';
import { useSaveApprovalSearch } from '@/hooks/apiHooks';
import TabSwitch from '@/components/Approval/Content/TabSwitch';
import DocumentFilters from '@/components/Approval/Content/DocumentFilters';
import ApprovalFilters from './ApprovalFIlters';
import DocumentContents from '@/components/Approval/Content/DocumentContents';
import { GNBHeightNumber } from '@/components/@IntranetLibrary/GNB/styles';
import { WORK_MEETING_NOTES_TAB } from '@/constant/url';
import { NATIVE_VIEW_APPROVAL_TYPES } from '@/constant';

const Content = () => {
  const router = useRouter();

  const [filters, setFilters] = useAtom(approvalFilter);

  const {
    handleBookMark,
    approvalData,
    approvalMutate,
    bookMarkData,
    isBookMarkLoading,
    doApprovalData,
    isDoSearchApprovalLoading,
    ingData,
    isIngSearchApprovalLoading,
    unreadReffererData,
    isRefererSearchApprovalLoading,
    myRefererMutate,
    temporaryApproval,
    temporaryApprovalLoading,
    setCurrentPage,
    currentPage,
    dataItemChange,
    setFilter,
  } = useApproval();

  // 메일·푸시 딥링크(?approvalFilter=to-approve)로 진입하면 좌측 필터를 맞춰 준다.
  useApprovalFilterLink(setFilter);

  const { showCompletedDot, showRejectedDot } = useAuthorStatusBadgeDot();

  const handleCategoryClick = (id: number) => {
    setFilter(id);
  };

  const methods = useForm<SearchData>({
    defaultValues: { keyword: '', searchFields: 'subject,content' },
  });

  const onSubmit = (org: SearchData) => {
    const { keyword, searchFields } = org;
    setFilters({
      ...filters,
      keyword: keyword,
      searchFields: searchFields,
    });
    useSaveApprovalSearch(keyword);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const isLoading =
    isBookMarkLoading ||
    temporaryApprovalLoading ||
    isDoSearchApprovalLoading ||
    isIngSearchApprovalLoading ||
    isRefererSearchApprovalLoading;

  const doApprovalCount =
    doApprovalData?.meta.totalCount && doApprovalData?.meta.totalCount > 0
      ? doApprovalData?.meta.totalCount
      : undefined;
  const ingApprovalCount =
    ingData?.meta.totalCount && ingData?.meta.totalCount > 0
      ? ingData?.meta.totalCount
      : undefined;
  const myRefererCount =
    unreadReffererData?.meta.totalCount &&
    unreadReffererData?.meta.totalCount > 0
      ? unreadReffererData?.meta.totalCount
      : undefined;
  const bookMarkCount =
    bookMarkData?.meta.totalCount && bookMarkData?.meta.totalCount > 0
      ? bookMarkData?.meta.totalCount
      : undefined;
  const temporaryCount =
    temporaryApproval?.meta.totalCount && temporaryApproval?.meta.totalCount > 0
      ? temporaryApproval?.meta.totalCount
      : undefined;

  const handleClickPage = (pageNum: number) => {
    setCurrentPage(pageNum);
  };

  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.style.paddingLeft = '0';
      mainRef.current.style.paddingRight = '0';
    }
  }, []);

  const [docId, setDocId] = useState('');
  const [approvalType, setApprovalType] = useState<string | undefined>(
    undefined,
  );
  const approvalModalRef = useRef<ModalRef>(null);
  const { openModal: openApprovalModal, closeModal: closeApprovalModal } =
    useModal(approvalModalRef);

  /*
   * 레거시 양식은 다음 문서 선정이 레거시 몫이라 프리페치가 쓰이지도 않고,
   * 포탈이 따로 고르면 레거시 큐와 어긋남.
   */
  const isNativeType =
    !!approvalType && NATIVE_VIEW_APPROVAL_TYPES.includes(Number(approvalType));
  const { nextDoc, promptNext, clearNextDoc } = useNextApprovalDoc(
    docId,
    isNativeType,
  );

  /** 모달은 닫지 않음 — docId/approvalType 스왑으로 같은 모달에 다음 문서 로드 */
  const openDoc = (next: NextApprovalDoc) => {
    setDocId(next.docId);
    setApprovalType(
      next.typeCode === undefined ? undefined : String(next.typeCode),
    );
    clearNextDoc();
  };

  const handleApprovalOpen = (docId: string, approvalType?: string) => {
    setDocId(docId);
    setApprovalType(approvalType);
    clearNextDoc(); // 목록에서 새로 여는 흐름 — 직전 결재의 응답이 늦게 와서 덮어쓰지 않게
    openApprovalModal();
    //내가 참조자인 문서 Count Fetch 하기위해..
    setTimeout(() => {
      approvalMutate();
      myRefererMutate();
    }, 5000);
  };

  const handleDecided = async (
    decidedDocId: string,
    action: ApprovalDecisionAction,
  ) => {
    // 보류(H)는 결재 큐에 남아 본인이 재처리 — 상태 패치 대상도 아님.
    if (action === 'hold') return;
    const status = action === 'reject' ? 'R' : 'C';
    dataItemChange(decidedDocId, { status }); // 결재할문서/전체 리스트에서 C·R 제거
    const { next, stale } = await promptNext(decidedDocId, action);
    if (!next && !stale) {
      closeApprovalModal();
    }
  };

  const isDocument = useMemo(
    () => router.query.subtab === WORK_MEETING_NOTES_TAB,
    [router.query.subtab],
  );

  return (
    <>
      <HoverLeftDrawerLayout
        mainRef={mainRef}
        newHomeMode={true}
        isSliding={true}
        contentMinWidth={640}
        gnbHeight={32 + 24 + GNBHeightNumber}
        leftContent={
          <LeftContainer tabComponent={<TabSwitch isDocument={isDocument} />}>
            {isDocument ? (
              <DocumentFilters />
            ) : (
              <ApprovalFilters
                onClick={handleCategoryClick}
                isLoading={isLoading}
                doApprovalCount={doApprovalCount}
                ingApprovalCount={ingApprovalCount}
                myRefererCount={myRefererCount}
                bookMarkCount={bookMarkCount}
                temporaryCount={temporaryCount}
                showCompletedDot={showCompletedDot}
                showRejectedDot={showRejectedDot}
                // 신규 행 추가는 patch(dataItemChange) 불가 — 리스트 revalidate 로 반영.
                onDocumentSubmitted={() => approvalMutate()}
                dataItemChange={dataItemChange}
              />
            )}
          </LeftContainer>
        }
        rightContent={
          isDocument ? (
            <DocumentContents />
          ) : (
            <FormProvider {...methods}>
              <RightContainer
                data={approvalData}
                currentPage={currentPage}
                handleClickPage={handleClickPage}
                handleApprovalOpen={handleApprovalOpen}
                handleBookMark={handleBookMark}
                onSubmit={onSubmit}
                handleCategoryClick={handleCategoryClick}
              />
            </FormProvider>
          )
        }
      />
      <ApprovalModal
        ref={approvalModalRef}
        url={getIntranetDocUrl(docId, true)}
        docId={docId}
        approvalType={approvalType}
        dataItemChange={dataItemChange}
        handleClose={() => {
          clearNextDoc(); // 결재 응답 대기 중 닫으면 뒤늦게 레이어가 뜸
          closeApprovalModal();
        }}
        onDecided={handleDecided}
        onOpenNextDoc={openDoc}
      />
      <NextApprovalConfirmModal
        open={!!nextDoc}
        onOpenNext={() => {
          if (!nextDoc) return;
          openDoc(nextDoc);
        }}
        onClose={() => {
          clearNextDoc();
          closeApprovalModal();
        }}
      />
    </>
  );
};

export default Content;
